import { useState, useEffect, Suspense, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import VersionBanner, { VersionPill } from "@/components/app/VersionBanner";
import {
  LayoutDashboard, Users, FileText, CreditCard, Landmark,
  CalendarCheck2, FileDown, Settings, ScrollText, LogOut, ShieldCheck,
  Stethoscope, Menu, X, FileBarChart2, Calculator, ClipboardCheck, BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/guide", label: "Guía", icon: BookOpen, testid: "nav-guide", collabOnly: true },
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/services", label: "Catalogue", icon: Stethoscope, testid: "nav-services" },
  { to: "/invoices", label: "Factures", icon: FileText, testid: "nav-invoices" },
  { to: "/payments", label: "Paiements", icon: CreditCard, testid: "nav-payments" },
  { to: "/reconciliation", label: "Rapprochement", icon: Landmark, testid: "nav-reconciliation" },
  { to: "/calendly", label: "Rendez-vous", icon: CalendarCheck2, testid: "nav-calendly" },
  { to: "/accounting", label: "Comptabilité", icon: Calculator, testid: "nav-accounting" },
  { to: "/validations", label: "Validations", icon: ClipboardCheck, testid: "nav-validations", badge: true },
  { to: "/imports", label: "Imports", icon: FileDown, testid: "nav-imports" },
  { to: "/exports", label: "Exports", icon: FileDown, testid: "nav-exports" },
  { to: "/ereporting", label: "E-reporting", icon: FileBarChart2, testid: "nav-ereporting" },
  { to: "/audit", label: "Audit", icon: ScrollText, testid: "nav-audit" },
  { to: "/settings", label: "Paramètres", icon: Settings, testid: "nav-settings", adminOnly: true },
];

export default function AppLayout() {
  const { user, logout, isAdmin, changeLang } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchPending = useCallback(async () => {
    try {
      const r = await api.get("/approvals/count");
      setPendingCount(r.data?.pending || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPending();
    const iv = setInterval(fetchPending, 60000);
    window.addEventListener("fc:approvals-changed", fetchPending);
    return () => {
      clearInterval(iv);
      window.removeEventListener("fc:approvals-changed", fetchPending);
    };
  }, [fetchPending]);

  // Close drawer on route change
  const onNavClick = () => setDrawerOpen(false);

  const navItems = NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.collabOnly || !isAdmin));
  const lang = user?.lang || "fr";

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-primary)" }}>
      {/* Mobile top bar */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 border-b"
        style={{ background: "var(--bg-surface)", borderColor: "var(--border-base)" }}
      >
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 rounded-md"
          style={{ minHeight: 44, minWidth: 44 }}
          aria-label="Ouvrir le menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2" data-testid="brand-logo-mobile">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "var(--brand)" }}>
            <ShieldCheck className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold text-sm">FactureCab</span>
        </button>
        <div style={{ minWidth: 44 }} />
      </div>

      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setDrawerOpen(false)}
          data-testid="mobile-drawer-backdrop"
        />
      )}

      {/* Sidebar — drawer on mobile, fixed on desktop */}
      <aside
        className={`fixed lg:static top-0 left-0 z-50 h-full w-[260px] shrink-0 border-r flex flex-col transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        style={{ borderColor: "var(--border-base)", background: "var(--bg-surface)" }}
      >
        <div className="px-5 py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-base)" }}>
          <button onClick={() => { navigate("/dashboard"); onNavClick(); }} className="flex items-center gap-2" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--brand)" }}>
              <ShieldCheck className="w-4 h-4 text-white" aria-hidden />
            </div>
            <div className="text-left">
              <div className="font-display font-extrabold text-[15px] leading-none">FactureCab</div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">EURL · privé</div>
            </div>
          </button>
          <button
            data-testid="mobile-close-drawer"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden p-2 -mr-2"
            style={{ minHeight: 44, minWidth: 44 }}
            aria-label="Fermer le menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              onClick={onNavClick}
              className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
            >
              <n.icon className="w-4 h-4" aria-hidden />
              <span>{n.label}</span>
              {n.badge && pendingCount > 0 && (
                <span
                  data-testid="approvals-badge"
                  className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: "#F59E0B", color: "#fff", minWidth: 18, textAlign: "center" }}
                >
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t" style={{ borderColor: "var(--border-base)" }}>
          <div className="flex items-center justify-between px-2 mb-2" data-testid="lang-switcher">
            <span className="text-[10px] uppercase tracking-widest text-stone-400">Langue</span>
            <div className="flex rounded-md border overflow-hidden" style={{ borderColor: "var(--border-base)" }}>
              <button
                data-testid="lang-fr-btn"
                onClick={() => changeLang("fr")}
                className="px-2 py-1 text-[11px] font-semibold"
                style={lang === "fr" ? { background: "var(--brand)", color: "#fff" } : { color: "var(--text-secondary)" }}
              >
                FR
              </button>
              <button
                data-testid="lang-es-btn"
                onClick={() => changeLang("es")}
                className="px-2 py-1 text-[11px] font-semibold"
                style={lang === "es" ? { background: "var(--brand)", color: "#fff" } : { color: "var(--text-secondary)" }}
              >
                ES
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2 px-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-stone-200" />
            )}
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate" data-testid="current-user-name">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-stone-500 truncate">
                {isAdmin ? "Administrateur" : "Collaborateur"} · {user?.email}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="sidebar-link w-full"
            style={{ color: "var(--text-secondary)" }}
          >
            <LogOut className="w-4 h-4" aria-hidden />
            <span>Déconnexion</span>
          </button>
          <VersionPill />
        </div>
      </aside>

      {/* Main content — pushed down on mobile to clear top bar */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <VersionBanner />
        <Suspense fallback={<div className="p-8 text-sm text-stone-500">Chargement…</div>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
